import __webpack_public_path__ from "./public-path";
import Animations, {doFlipAnimation} from "./Animations";
import Links from "./Links";
import UserEvents from "./UserEvents";
import ScrollListener  from "./ScrollListener";
import Embeds from "./Embeds";
import Message from './Message';
import Dialog from "./Dialog";
import Components from "./Components";
import CONSTANTS, {PAGETYPES} from "./Constants";
import Scrub from "./Scrub";

const ChartsPromise = () => import(/* webpackChunkName: "charts" */ './Charts.js');

export default class Scene {

    constructor(spread, uuid, animations = {}, videos, scrubs, layout ){
        this.uuid = uuid;
        this.spread = spread;
        this.status = CONSTANTS.STATUS.BLANK;
        this.layoutType = layout;

        this.restart = (animations.settings && animations.settings.restart) || false; // in screenshot mode the animations.settings object is undefined

        const scene_element = this.getSceneElement();
        const data = scene_element.data();
        this.pagetype = data.pagetype;
        this.autoPlay = (layout === 1 && data.autoplay) || false;
        this.sticky = scene_element.hasClass("sticky_scene"); // is sticky?

        this.animations = new Animations(this, animations);

        if(videos.length > 0){
            import(/* webpackChunkName: "videos" */ './Videos').then(({ default: Videos }) => {
                this.videos = new Videos(this, videos);
                if(this.status >= CONSTANTS.STATUS.READY){
                    this.videos.preloadVideo();

                    if(this.status === CONSTANTS.STATUS.STARTED){
                        this._videosClassLoaded(scene_element);
                    }else{
                        for (let id in this.videos.players) {
                            this.videos.stopVideo(id, true);
                        }
                        this.videos.removeAllListeners(CONSTANTS.EVENTS.AUDIO_STATE_CHANGED);
                    }
                }
            }).catch(error => console.error('An error occurred while loading the component', error));
        }

        let audios = scene_element.find(".audio");
        if(audios.length > 0){
            import(/* webpackChunkName: "audios" */ './Audios').then(({ default: Audios }) => {
                this.audios = new Audios(this, audios);
            }).catch(error => console.error('An error occurred while loading the component', error));
        }

        // this.slideshows = new Slideshows(this);
        let slideshows = scene_element.find(".slideshow");
        if(slideshows.length > 0){
            import(/* webpackChunkName: "slideshows" */ './Slideshows').then(({ default: Slideshows }) => {
                this.slideshows = new Slideshows(this, slideshows);
                if(this.status >= CONSTANTS.STATUS.READY){
                    this.slideshows.initSlideshows();

                    if (this.status === CONSTANTS.STATUS.STARTED) {
                        this.slideshows.startAutoPlaySlideshows( scene_element, this.pagetype );
                    }
                }
            }).catch(error => console.error('An error occurred while loading the component', error));
        }

        this.embeds = new Embeds(this);

        Object.defineProperty(this, 'inViewport', {
            get: function() { return this._inViewport; },
            set: function(newValue) {
                let changed = this._inViewport !== newValue;
                this._inViewport = newValue;

                if(changed){

                    if(this._inViewport){
                        this.start();
                        if(!this.isDwarf){
                            this.spread.setCurrentSceneOnly(this);
                        }
                    }else{
                        this.stop();
                        this.spread.notTheCurrentSceneAnymore(this);
                    }
                }
            },
        })

        this._dialogs = {};
        this.scrub = new Scrub(this, scrubs);
    }

    init(){
        if( !this.inited ){

            this.animations.init();

            this.scrub.init()

            this.bindButtons();

            this.slideshows && this.slideshows.initSlideshows( );
            this.embeds.initEmbeds();
            this.initCharts();
            this.initComponents();

            if( this.spread.active_layout === 1 && this.spread.getSettings().data_domain !== undefined) {
                this.preloadHoverImages(this.spread.getSettings().data_domain);
            }

            this.inited = true;
            this.ready();
            this.videos && this.videos.preloadVideo();
        }

        return this; //chainable
    }

    ready(){
        if( this.status < CONSTANTS.STATUS.READY){
            this.status = CONSTANTS.STATUS.READY;
            this.spread.ready();
        }

        return this; //chainable
    }

    start(){
        if(this.status === CONSTANTS.STATUS.STARTED) {
            return;
        }

        this.init();

        let scene_element = this.getSceneElement();

        if( !this.sticky && !this.isDwarf ){
            scene_element.addClass("current_scene scene_active").removeClass("first_scene");
        }
        this.animations.startAnimations( );
        this.components && this.components.start();


        if( this.pagetype === CONSTANTS.PAGETYPES.LONGPAGE || this.spread.active_layout === 2 ){
            let scroller = this.spread.getScrollContainer(true) || scene_element;

            this.scrollListener = new ScrollListener({
                scene: scene_element,
                scroller: scroller,
                bodyScroll: this.spread.scrollingBody,
                scrollerHeight: this.spread.getWindowHeight(),
                scale: this.spread.active_layout === 2 ? this.getSizeData().scale : 1
            });

            this.scrollListener.on("scrolled", (data) => {
                this.longpageScrolled(data.target, data.data)
            })
        }

        this.status = CONSTANTS.STATUS.STARTED;

        this.audios && this.audios
            .removeAllListeners( CONSTANTS.EVENTS.AUDIO_STATE_CHANGED )
            .on( CONSTANTS.EVENTS.AUDIO_STATE_CHANGED, (playing_audios) => {
                // check playing audios + playing videos > 0
                if( (playing_audios + (this.videos && this.videos.getPlayingCount() || 0)) > 0){
                    Message.sendMessage({message: "INSPREADAUDIO_PLAYING"}, this.spread.spread_id);
                }else{
                    Message.sendMessage({message: "INSPREADAUDIO_STOPPED"}, this.spread.spread_id);
                }
            } )
        ;

        this.videos && this._videosClassLoaded(scene_element);
        this.slideshows && this.slideshows.restartEffect() && this.slideshows.startAutoPlaySlideshows( scene_element, this.pagetype );
        this.components && this.components.startComponents()
        this.scrub.enableAll()

        Embeds.fireIframeEventInContext(scene_element, "START");
        Embeds.fireIframeEventInContext(scene_element, "TOGGLE_ON", (i, el) => {
            return this.embeds.filterIframeContext(i, el, this.spread.getWindowHeight(), true)
        });

        this.charts && this.charts.animateVisible();

        Message.sendMessage({message:"SCENE_STARTED", scene_id: this.uuid, bodyScrollLock: this.spread.getScrollLock()}, this.spread.spread_id);

        if( this.pagetype === CONSTANTS.PAGETYPES.LONGPAGE || this.spread.active_layout === 2 ){
            let scroll_data = {
                top : this.getCurrentScroll()
            };
            scroll_data.bottom = scroll_data.top + this.spread.getWindowHeight();
            this.longpageScrolled( scene_element, scroll_data);
        }

        if (this.autoPlay) {
            this.autoPlayTimeout = setTimeout(() => {
                this.status === CONSTANTS.STATUS.STARTED && this.spread.sceneLink('NEXT')
            }, this.autoPlay)
        }
    }

    pause(){

        if(this.status === CONSTANTS.STATUS.STARTED) {
            let scene_element = this.getSceneElement();
            Embeds.fireIframeEventInContext(scene_element, "STOP");
            this.slideshows && this.slideshows.stopSlideshows(scene_element);
            this.components && this.components.stop()
            this.autoPlayTimeout && clearTimeout(this.autoPlayTimeout)

            this.animations.pause();

            this.scrollListener && this.scrollListener.destroy().removeAllListeners("scrolled");

            if(this.videos){
                for (let id in this.videos.players) {
                    this.videos.stopVideo(id, true);
                    this.videos.resetPlayed(id)
                }
                this.videos.removeAllListeners(CONSTANTS.EVENTS.AUDIO_STATE_CHANGED);
            }

            this.audios && this.audios.removeAllListeners(CONSTANTS.EVENTS.AUDIO_STATE_CHANGED);

            this.scrub.disableAll();

            scene_element.removeClass("current_scene");
            this.status = CONSTANTS.STATUS.PAUSED;
        }
    }

    stop(force){

        if(this.status > CONSTANTS.STATUS.STOPPED){

            let scene_element = this.getSceneElement();
            Embeds.fireIframeEventInContext(scene_element, "STOP");
            this.slideshows && this.slideshows.stopSlideshows(scene_element);
            // this.components && this.components.stopComponents()
            this.components && this.components.stop();
            this.autoPlayTimeout && clearTimeout(this.autoPlayTimeout)

            //reset all Toggled elements
            this.resetToggled(scene_element, force || this.restart );

            this.animations.reset( force );
            if( force || this.restart ){
                this.charts && this.charts.resetAll();
            }

            this.scrollListener && this.scrollListener.destroy().removeAllListeners("scrolled");

            this.audios && this.audios.stopAll();

            if(this.videos){
                for(let id in this.videos.players){
                    const wasPlaying = this.videos.stopVideo(id, true);
                    if(wasPlaying && this.pagetype === PAGETYPES.LONGPAGE && this.videos.players[id].mute){
                        this.videos.players[id].element.addClass("paused-by-scroll")
                    }
                }
                this.videos.removeAllListeners( CONSTANTS.EVENTS.AUDIO_STATE_CHANGED);
            }

            this.audios && this.audios.removeAllListeners( CONSTANTS.EVENTS.AUDIO_STATE_CHANGED);

            scene_element.removeClass("current_scene scene_active");

            if(this.restart && this.pagetype === CONSTANTS.PAGETYPES.LONGPAGE && !this.spread.scrolling_scenes){
                this.getSceneElement(true).scrollTop(0);
            }

            this.scrub.disableAll();

            this.status = CONSTANTS.STATUS.STOPPED;
        }
    }

    _videosClassLoaded(scene_element){
        if(!this._afterVideoClassLoaded){
            this._afterVideoClassLoaded = true;

            this.videos && this.videos
                .removeAllListeners( CONSTANTS.EVENTS.VIDEO_STATE_CHANGED )
                .on( CONSTANTS.EVENTS.VIDEO_STATE_CHANGED, (playing_videos) => {
                    // check playing videos + playing audios > 0
                    if( (playing_videos + (this.audios && this.audios.getPlayingCount() || 0) ) > 0){
                        Message.sendMessage({message: "INSPREADAUDIO_PLAYING"}, this.spread.spread_id);
                    }else{
                        Message.sendMessage({message: "INSPREADAUDIO_STOPPED"}, this.spread.spread_id);
                    }
                } )
            ;


            this.videos && this.videos.scaleBackgroundVideos(scene_element);
        } else {
            this.videos.rebindIframePlayers()
        }
        this.videos && this.videos.startAutoPlayVideos(scene_element);
    }

    destroy(){
        this.videos && this.videos.destroy();
    }


    /**
     * Bind all buttons that have a data-attribute openstate or closestate. Also check for the openfirst data attribute to open the first state
     * @method bindStateButtons
     */
    bindButtons() {
        this.getSceneElement()
        // .off('click.maglr mouseenter.maglr mouseleave.maglr')
            .on('click.maglr',          "[data-internal-link]",         (event) => {
                let data = $(event.currentTarget).data("internal-link")
                if (this.spread.spread_id === data.spread_id && data.scene_id) {
                    this.spread.sceneLink(data.scene_id, event.currentTarget)
                } else {
                    Links.internalLink(event, this.spread.getSettings() )
                }
            } )
            .on('click.maglr',          "[data-external-link]",         (event) => Links.externalLink(event, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-reference-link]",        (event) => Links.referenceLink(event, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-interface-link]",        (event) => Links.interfaceLink(event, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-togglestate]",           (event) => this.toggleStateTrigger(event) )
            .on('click.maglr',          "[data-action-link]",           (event) => Links.triggerElementAction(event, this, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-scene-link]",            (event) => this.spread._sceneLink(event) )
            .on('click.maglr',          "[data-file-link]",             (event) => Links.fileLink(event, this.spread.getSettings()) )
            .on('click.maglr',          "[data-social-link]",           (event) => Links.socialLink(event, this.spread.top_domain, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-scrollto-link]",         (event) => Links.scrollToLink(event, this.spread, this.spread.getSettings(), this.spread.getInterfaceOffset() ) )
            .on('click.maglr',          "[data-postmessage-link]",      (event) => Links.postMessageLink(event, this.spread.getSettings() ) )
            .on('click.maglr',          "[data-gotostate]",             (event) => this.goToStateEvent(event) )
            .on('click.maglr',          "[data-multi-action-link]",     (event) => this.spread.multiActionLinkEventHandler(event) )
            .on('mouseenter.maglr',     "[data-hovertogglestate]",      (event) => this.hoverToggleState(event) )
            .on('mouseleave.maglr',     "[data-hovertogglestate]",      (event) => this.hoverToggleState(event) )
            .on('child-animation-done', ".group.wait-for-child-close",  (event) => this.animations.startParentCloseAnimation(event) )
            .on('click.maglr',          "a",                            (event) => Links.trackClickedLink(event, this.spread.getSettings() ) )
            .on('keyup.maglr',          "[role='button']",              (event) => this.keyboardTrigger(event) )
            .on('keyup.maglr',          "[role='link']",                (event) => this.keyboardTrigger(event) )
        ;
    }

    keyboardTrigger(event) {
        const key = event.key || event.keyCode;
        if (!this._noKeyUp && key) {
            switch (key) {
                case CONSTANTS.KEYS.ENTER:
                case CONSTANTS.KEYS.CODES.ENTER:
                case CONSTANTS.KEYS.SPACE:
                case CONSTANTS.KEYS.CODES.SPACE:
                    $(event.currentTarget).trigger('click');
                break;
            }
        }
    }

    preloadHoverImages(data_domain) {
        this.images = this.runSelector("[data-hover-image]").map(function (i, el) {
            return el.dataset.hoverImage;
        }).get();

        if (this.images.length > 0) {
            this.images.forEach( (image) => {
                image = new Image().src = data_domain + image;
            })
        }
    }

    longpageScrolled(target, data){
        let offsetTop = this.spread.getInterfaceOffset().top;
        data.top += offsetTop;
        data.bottom += offsetTop;

        // this.animations.animateByScroll(data);

        // if( (!this.spread.scrolling_scenes && this.spread.active_layout === 1) || (this.spread.embeddedWithJsControl && this.spread.embeddedType === CONSTANTS.EMBEDTYPES.FULLHEIGHT) ){
        //     this.setStickiesActive(data);
        // }


        this.videos && this.videos.startAutoPlayVideosOnLongpageScroll(data);
        this.slideshows && this.slideshows.startAutoPlaySlideshowOnLongpageScroll(target, data);

        this.charts && this.charts.animateByLongpageScroll(data);

        this.embeds.toggleOnByScroll();
    }

    runSelector(selector){
        let sceneElement = this.getSceneElement(true);
        if(sceneElement.is(selector)){
            return sceneElement;
        }else{
            return sceneElement.find(selector);
        }
    }

    getSceneElement(as_jquery = true){
        let scene = this.spread.runSelector( '#'+this.uuid );
        if(as_jquery){
            return scene
        }else{
            return scene[0];
        }
    }

    getMaglrSpread(){
        return this.getSceneElement(true).children(".maglrspread");
    }

    /**
     * Get the currentTarget's data togglestate and call the toggleState function with it.
     * Should only be called by a click event binding
     * @param event
     */
    toggleStateTrigger(event) {
        event.stopImmediatePropagation();
        this.toggleState($(event.currentTarget).data(), event.currentTarget);
    }

    /**
     * Toggle the elements in the given data object.
     * @param {Object} data
     * @param {clickedTarget} clickedTarget
     * @param dispatchEventUpdate
     */
    async toggleState(data, clickedTarget, dispatchEventUpdate = true) {
        let i, length, toggled = [];

        const elements = [...new Set(data.togglestate.elementIds)]
        length = elements.length;
        let focusTarget = data.focustarget || elements[0];

        for (i = 0; i < length; i++) {
            let toggle = this.toggleElement("#" + elements[i], false, data.togglestate.mode);
            let element = toggle.element;
            if (element) {
                toggled.push(element);

                if(clickedTarget && elements[i] === focusTarget && toggle.isVisible){
                    const closeButton = this._injectCloseButton(element, clickedTarget, elements);
                    if (closeButton) {
                        this._dialogs[clickedTarget.id] = new Dialog(element, closeButton);
                    }
                }
            }
        }

        //closeOption
        if (data.togglestate.closeOption === 'closeOthers') {
            this.runSelector(".group[data-hidden='1']:not(.delay_by_timeline)").not(toggled).each((i, element) => {
                this.toggleElement(element, true);
            });
        } else if (data.togglestate.closeOption === 'closeLevel') {
            for (i = 0; i < toggled.length; i++) {
                //close all sibling groups that are not in the toggled array
                let jq_element = $(toggled[i]),
                    siblings = this._findSiblingGroups(jq_element)
                ;

                siblings.not(toggled).each( (i, element) => {
                    this.toggleElement(element, true);
                });
            }
        }

        if (dispatchEventUpdate && toggled.length > 0) {
            UserEvents.sendEventUpdate('Link', 'Toggle-layer', toggled.map(function (element) {
                return element.getAttribute('data-label')
            }).join(", "), this.spread.getSettings(), clickedTarget);
        }
        return toggled
    }

    _findSiblingGroups(element) {
        //step 1: find the first matching parent (a group or the maglrspread)
        let context = element.parents(".group, .maglrspread").first();

        //step 2: find all sibling groups (not self)
        //step 3: filter the result by checking the number of parents (.group) between the context and the element
        return context.find(".group").not(element).filter(function (i, el) {
            return $(el).parentsUntil(context, ".group").length === 0
        });
    }

    hoverToggleState(event) {
        event.stopImmediatePropagation();
        let i, length, data = $(event.currentTarget).data("hovertogglestate");
        length = data.elementIds.length;
        for (i = 0; i < length; i++) {
            this.toggleElement("#" + data.elementIds[i], false, data.mode);
        }
    }

    setVisibilityAndAction(element, setVisible) {
        let jq_element = $(element);
        const wasVisible = jq_element.hasClass('hidden')
        if (setVisible) {
            jq_element.removeClass('hidden').addClass('toggled');
        } else {
            jq_element.addClass("hidden toggled");
        }
        this.afterElementToggle(jq_element, setVisible, setVisible ? "TOGGLE_ON" : "TOGGLE_OFF");
        return wasVisible !== setVisible; // Returns true when value has changed
    }

    toggleAndAction(element) {
        let jq_element = $(element);
        if(jq_element.addClass("toggled").toggleClass("hidden").hasClass("hidden")){
            this.afterElementToggle(jq_element, false, "TOGGLE_OFF");
            return false
        }else{
            this.afterElementToggle(jq_element, true, "TOGGLE_ON");
            return true
        }
    }

    /**
     *
     * @param {String|HTMLElement} element
     * @param {boolean} force_hide
     * @param {String} mode
     */
    toggleElement(element, force_hide, mode = 'toggle') {
        let jq_element = this.getSceneElement().find(element)
        if (jq_element.length < 1) {
            return {element: undefined, isVisible: false};
        }
        const isVisible = !jq_element.hasClass("hidden")
        const setVisible = (mode === 'toggle') ? !isVisible : (mode === 'show') // visibility will be set to the invert of isVisible when mode is set to toggle
        //check if force_hide is true and the element is already hidden just return the element, no action needed.
        if (force_hide === true && isVisible === false) {
            return {element: jq_element.get(0), isVisible};
        }

        let uuid = jq_element.attr("id");

        if (jq_element.hasClass("animating")) {
            const animatingType = jq_element.data("animatingType");
            const openAnimation = this.animations.getOpenAnimation(uuid);
            const closeAnimation = this.animations.getCloseAnimation(uuid);
            if ( ['in', 'in-children'].includes(animatingType) && openAnimation) {
                openAnimation.progress(1);
            } else if (animatingType === "out" && closeAnimation) {
                closeAnimation.progress(1);
                jq_element.addClass("hidden toggled");

                if(force_hide){
                    return {element: jq_element.get(0), isVisible: false};
                }
            }
        }

        if (isVisible && !setVisible) {
            if (jq_element.hasClass("has_animation_close") || jq_element.children(".has_animation_close").length > 0) {
                if (!jq_element.hasClass("animating")) {

                    if (this.animations.getCloseAnimation(uuid)) {
                        const closeLine = this.animations.moveCloseToGlobal(uuid)
                        closeLine.paused(false).restart()
                    } else {

                        const children = jq_element.children(".has_animation_close:not(.hidden)")
                            .add(jq_element.children(".mask-animation-wrap, a.link, .mask").children(".has_animation_close:not(.hidden)"))
                        ;

                        if (children.length > 0) {
                            jq_element.addClass("wait-for-child-close").data('wait-for-child-close-count', children.length);
                            for (let i = 0; i < children.length; i++) {
                                const childCloseAnimation = this.animations.getCloseAnimation(children[i].id);
                                if (childCloseAnimation) {
                                    const closeLine = this.animations.moveCloseToGlobal(children[i].id)
                                    closeLine.paused(false).restart()
                                }
                            }
                        } else {
                            this.setVisibilityAndAction(jq_element, setVisible);
                        }
                    }
                }
            } else if (!this.toggleAndAction(jq_element) && !force_hide) {
                const dialogId = Object.keys(this._dialogs).find((uuid) => this._dialogs[uuid].element === jq_element[0])
                dialogId && this.focusUuid(dialogId)
            }

            //check if element has in animation. add that animation to the open animations list
            let has_in_children = jq_element.children(".has_animation_in, .delay_by_timeline").add(jq_element.children(".mask-animation-wrap, a.link, .mask").children(".has_animation_in, .delay_by_timeline"));
            if (( jq_element.hasClass("has_animation_in") || (jq_element.hasClass("has_animation_open") && jq_element.hasClass("delay_by_timeline")) || has_in_children.length > 0 ) && !this.animations.getOpenAnimation(uuid)) { //
                this.animations.addInAnimationAsOpen(jq_element, uuid);
            }
        } else if (!isVisible && setVisible) {
            //$("[data-togglestate*='"+jq_element.attr('id')+"']").addClass("active"); // don't toggle for groups/layers anymore
            this.resetToggled(jq_element, true); // reset to original state
            if (jq_element.hasClass("has_animation_open")){
                if (!jq_element.hasClass("animating")) {

                    // prevents flickering when transition-effect and toggled with open-animation
                    jq_element.addClass("to_be_animated");

                    //only toggle when hidden and the toggle will not be called by the timeline because of a delay
                    if(jq_element.hasClass("hidden") && !jq_element.hasClass("timeline_delay") ){

                        //Extra check because we dont want the toggle when the element only has a delay without an actual animation. (Broke into 2 separate if's for readability)
                        if (  !jq_element.hasClass("delay_by_timeline") || jq_element.hasClass("has_animation_in")  ){
                            this.setVisibilityAndAction(jq_element, setVisible);
                        }
                    }
                    //start open timeline if the element has one
                    if (this.animations.getOpenAnimation(uuid)) {
                        this.animations.getOpenAnimation(uuid).restart();
                    }
                }
            } else {
                this.setVisibilityAndAction(jq_element, setVisible);
            }
        }
        return {element: jq_element.get(0), isVisible: setVisible};
    }

    resetToggled(context, force) {
        //remove all hidden classes so that toggled elements are showed.
        let hidden = $(context).find(".hidden:not([data-hidden])");
        if (!force) {
            hidden = hidden.not(".has_animation_out");
        }
        hidden.removeClass("hidden toggled");

        //then set all elements that need to be hidden to the hidden class.
        let original_hidden = $(context).find("[data-hidden]");
        if (!force) {
            original_hidden = original_hidden.not('.delay_by_timeline');
        }
        original_hidden.removeClass("toggled").addClass("hidden");
    }

    /**
     * Execute actions after an element has been toggled or animated in/out like videos start/stop and embed messages
     *
     * @param {jQuery} jq_element
     * @param {boolean} visible
     * @param {string} embed_message
     * @param {mixed} filter optional element filter for elements inside group
     */
    afterElementToggle(jq_element, visible, embed_message, filter= false){
        if( visible ){
            jq_element.removeClass("delayed_animation");
            this.videos && this.videos.startAutoPlayVideos(jq_element);
            this.slideshows && this.slideshows.startAutoPlaySlideshows(jq_element, this.pagetype);


            if (jq_element.hasClass("embed") && jq_element.children(".file-embed, .url-embed").length > 0) { // single embed element without animation
                Embeds.fireIframeEvent(jq_element.children(".file-embed, .url-embed")[0], embed_message);
            }else if (jq_element.hasClass("group")) { // element is group

                //
                if( ! jq_element.hasClass("has_animation_open") && ! jq_element.hasClass("has_animation_in") ){
                    //no animation just send the message to the element or to filtered group children
                    Embeds.fireIframeEventInContext(jq_element, embed_message, (i, el) => {
                        return this.embeds.filterIframeContext(i, el, this.spread.getWindowHeight(), true);
                    });
                }else{
                    let check_for_animation = true;
                    if (embed_message === "ANIMATION_START" || embed_message === "ANIMATION_END") {

                        check_for_animation = false;
                    }
                    Embeds.fireIframeEventInContext(jq_element, embed_message, (i, el) => {
                        let filter_result = true;
                        if(filter){
                            filter_result = $(el).filter(filter).length === 1;
                        }
                        return filter_result && this.embeds.filterIframeContext(i, el, this.spread.getWindowHeight(), check_for_animation);
                    });
                }
            }

            //add reset-animation-counter class to reset the internal css counter. (this looks to be the only way it can be done)
            if(jq_element.hasClass("group")){
                jq_element.find(".loop-animation").removeClass("reset-animation-counter")
            }else if(jq_element.hasClass("loop-animation")){
                jq_element.removeClass("reset-animation-counter");
            }

        }else{
            this.videos && this.videos.stopVideosInContext(jq_element);
            this.animations.resetDelayedAnimations(jq_element);

            let loop;
            if (jq_element.hasClass("group")) { // element is group
                loop = jq_element.find(".loop-animation");
                Embeds.fireIframeEventInContext(jq_element, embed_message);
            } else {
                if (jq_element.children(".file-embed, .url-embed").length > 0) { // single embed element
                    Embeds.fireIframeEvent(jq_element.children(".file-embed, .url-embed")[0], embed_message);
                }

                if(jq_element.hasClass("loop-animation")){
                    loop = jq_element;
                }

            }

            if(loop){
                loop.addClass("reset-animation-counter").removeClass("loop-done");
            }
        }

        if (this.components) {
            if (jq_element.hasClass('group')) {
                jq_element[0].querySelectorAll('.maglr-webcomponent').forEach( element => this.components.visibilityUpdated(element))
            } else if (jq_element.hasClass('maglr-webcomponent')) {
                this.components.visibilityUpdated(jq_element[0])
            }
        }

        //find all toggle buttons that toggle the current element and set the aria-expanded to true
        const allControllingButtons = this.getSceneElement(false).querySelectorAll(`[aria-haspopup][aria-controls*="${jq_element[0].id}"]`)
        allControllingButtons.forEach(button => button.setAttribute('aria-expanded', visible))

        this.charts && this.charts.toggleChartsInContext(jq_element, visible);
    }

    goToStateEvent (event) {
        event.stopImmediatePropagation();
        this.goToState(JSON.parse(event.currentTarget.dataset.gotostate))
    }

    async goToState (goToStateLink) {
        const {state: stateName, options, elementIds} = goToStateLink

        // op basis van class
        // target.classList.add(`maglrstate-${stateName}`)

        // op basis van data attribuut
        // if (stateName === 'default') {
        //     target.removeAttribute('data-state')
        // } else {
        //     target.setAttribute('data-state', stateName)
        // }
        const spreadElement = this.spread.getSpreadElement(false)
        const targets = elementIds.map(uuid => spreadElement.querySelector(`#${uuid}`))

        doFlipAnimation(targets, stateName, options)

    }

    resized(){
        const scene = this.getSceneElement();

        if(this.pagetype !== CONSTANTS.PAGETYPES.STATICSIZE){
            this.slideshows && this.slideshows.resizeFullScreenSlideShows(scene);
            this.videos && this.videos.resizeFullScreenBackgroundVideos(scene);

            // this.resizeFullScreenFixedImages();
        }

        if(this.pagetype === CONSTANTS.PAGETYPES.LONGPAGE || this.spread.active_layout === 2 ){
            if( this.status === CONSTANTS.STATUS.STARTED ){
                this.scrollListener.updateOffset(this.spread.getWindowHeight(), this.spread.active_layout === 2 ? this.getSizeData().scale : 1);
            }

            // this.animations.updateByScrollAnimations( scale );

            // this.hasStickies() && this.calculateStickyFallback();
        }

        this.animations.updateMouseGyro(scene);
    }

    setFirstScene(){
        this.getSceneElement().addClass("first_scene");
        return this;
    }

    removeFirstScene(){
        this.getSceneElement().removeClass("first_scene");
        return this;
    }

    getBoundingClientRect(){
        return this.getSceneElement(false).getBoundingClientRect();
    }

    /**
     * Get the current scroll based on scroll- or transitioning-scenes
     *
     * @returns {Number}
     */
    getCurrentScroll(){
        if(this.spread.scrollingBody){
            let offsetTop = this.getSceneElement(false).offsetTop || 0
            if(this.spread.active_layout === 2 && this.getSizeData()){
                offsetTop *= this.getSizeData().scale;
            }
            return window.pageYOffset - offsetTop;
        }else if(this.spread.scrolling_scenes){
            //use scrollContainer scrollTop
            return this.spread.getScrollTop() - (this.getSceneElement(false).offsetTop || 0) // subtract the sceneOffset like the scroll does.
        }else{
            //use longpage scrollTop
            return this.getSceneElement().scrollTop();
        }

    }

    initCharts(){
        let charts = this.runSelector(".chart");
        if( charts.length > 0 ){
            ChartsPromise().then(({default: Charts}) => {
                this.charts = new Charts(this);
                this.charts.init(charts);

                this.chartsInited();
            }).catch((e) =>{
                console.error(e);
            });
        }
    }

    chartsInited(){
        if(this.status === CONSTANTS.STATUS.STARTED){
            this.charts.animateVisible();
        }
    }

    initComponents(){
        const components = this.runSelector(".maglr-webcomponent")
        if (components.length > 0) {
            this.components = new Components(this)
            this.components.init(components)
        }
    }

    setSceneSizeData(size_data){
        this.sizeData = size_data
    }

    getSizeData () {
        if (this.sizeData) {
            return this.sizeData
        }

        //no sizeData fallback
        const scene = this.getSceneElement()
        if (scene.length === 0) {
            const dimensions = scene.data('dimensions')
            return {
                height: dimensions.height,
                scale: 1,
            }
        }

        return {}
    }

    // resizeFullScreenFixedImages(){
    //     this.getSceneElement(true).find(".maglrsticky > .image.fullscreen-element, .maglrsticky > .image.fullheight-element").parent().height(this.spread.getWindowHeight());
    // }

    /*
     * Checks if the given element is in the viewport. the i parameter is for compatibility with the jQuery filter function
     *
     * @param {Object} el
     * @returns {boolean}
     */
    elementInViewport(el){
        if(this.status < CONSTANTS.STATUS.STARTED){
            return false;
        }
        let elRect =  el.getBoundingClientRect(), viewportRect;

        if(this.spread.scrollingBody){
            let offset = this.spread.getInterfaceOffset();
            viewportRect = {
                top : offset.top,
                bottom : this.spread.getWindowHeight() + offset.top
            };
        }else{
            viewportRect = this.spread.getSpreadElement(false).getBoundingClientRect();
        }
        return (elRect.bottom >= viewportRect.top) && (elRect.top <= viewportRect.bottom);
    }

    /**
     * Checks if the given element hidden. the i parameter is for compatibility with the jQuery filter function
     *
     * @param {Object} el
     * @returns {boolean}
     */
    isElementHidden(el){
        return $(el).closest(".hidden, .delayed_animation, .delayed_by_scroll").length > 0;
    }

    /**
     * Get the filter function to be used by jQuery.filter() if check_viewport is true then 'hidden' and viewport is checked.
     * If not only 'hidden' is checked.
     *
     * @param {boolean} check_viewport
     * @returns {Function}
     * @private
     */
    getElementFilterHiddenViewport(check_viewport){
        if(check_viewport){
            let inViewport = (i, el) => {
                return this.elementInViewport(el);
            };
            return (i, el) => {
                return !this.isElementHidden(el) && inViewport(i, el)
            }
        }else{
            return (i, el) => {
                return !this.isElementHidden(el);
            };
        }
    }

    _injectCloseButton(target, clickedTarget, elementIds) {
        console.log(target);
        if (target.classList.contains('group')) {
            const firstChild = target.firstElementChild;
            if (firstChild) {
                if (firstChild.classList.contains('a11yButton')) {
                   return firstChild;
                } else {
                    let a11yButton = document.createElement('button');
                    a11yButton.classList.add('a11yButton')
                    a11yButton.innerText = 'Close';
                    a11yButton.dataset.elementIds = JSON.stringify(elementIds);
                    a11yButton.dataset.origin = clickedTarget.id;
                    a11yButton.onclick = this._closeButtonClicked.bind(this);
                    return target.insertBefore(a11yButton, firstChild);
                }
            }
        }
    }

    _closeButtonClicked(event) {
        event.stopPropagation()
        event.preventDefault()
        console.log("CLOSE BUTTON", event, this);
        try{
            const elementIds = JSON.parse(event.target.dataset.elementIds);
            this.toggleState({togglestate : {elementIds : elementIds}}, event.currentTarget);
            this.focusUuid(event.target.dataset.origin)
            this._dialogs[event.target.dataset.origin] && this._dialogs[event.target.dataset.origin].releaseFocus();
            UserEvents.sendEventUpdate('Link', 'Accessibility', 'Close_layer_keyboard', this.spread.getSettings());
        } catch (e) {
            console.error(e)
        }
        return false
    }

    focusUuid (uuid) {
        this._noKeyUp = true
        setTimeout(() => {
            this._noKeyUp = false
        },100)
        this.getSceneElement(false).querySelector(`#${uuid}`).focus({preventScroll:true});
    }
}
